import { RoleName } from '@/common/const/Auth';
import { TFRole } from '@/lib/App';

export enum _Action {
  CREATE_VARIABLE,
  UPDATE_VARIABLE,

  CREATE_EXPERIMENT,
  UPDATE_EXPERIMENT,
  DELETE_EXPERIMENT,

  PAUSING_EXPERIMENT,
  APPROVE_EXPERIMENT,
  ABORT_EXPERIMENT,
  REJECT_EXPERIMENT,

  CREATE_ROLLOUT_DIFF,
  UPDATE_ROLLOUT_DIFF_APPROVAL_STATE,

  CONFIGURE_GROUP,
  ARCHIVE_VARIABLE,
}

const _ActionPermission: Record<_Action, { role: string }> = {
  [_Action.CREATE_VARIABLE]: { role: RoleName.EDITOR },
  [_Action.UPDATE_VARIABLE]: { role: RoleName.EDITOR },
  [_Action.ARCHIVE_VARIABLE]: { role: RoleName.PUBLISHER },

  [_Action.CREATE_EXPERIMENT]: { role: RoleName.EDITOR },
  [_Action.UPDATE_EXPERIMENT]: { role: RoleName.EDITOR },
  [_Action.DELETE_EXPERIMENT]: { role: RoleName.EDITOR },

  [_Action.APPROVE_EXPERIMENT]: { role: RoleName.PUBLISHER },
  [_Action.REJECT_EXPERIMENT]: { role: RoleName.PUBLISHER },
  [_Action.ABORT_EXPERIMENT]: { role: RoleName.PUBLISHER },
  [_Action.PAUSING_EXPERIMENT]: { role: RoleName.PUBLISHER },

  [_Action.CREATE_ROLLOUT_DIFF]: { role: RoleName.EDITOR },
  [_Action.UPDATE_ROLLOUT_DIFF_APPROVAL_STATE]: { role: RoleName.PUBLISHER },

  [_Action.CONFIGURE_GROUP]: { role: RoleName.PUBLISHER },
};

export enum UserAction {
  CREATE_A_VARIABLE,
  CREATE_VARIABLE_USING_CURRENT_TF,
  EDIT_CURRENT_VARIABLE,
  EDIT_CURRENT_VARIABLE_USING_CURRENT_TF,
  ARCHIVE_CURRENT_VARIABLE,
  UNDO_ARCHIVE_CURRENT_VARIABLE,

  CREATE_AN_EXPERIMENT,
  CLONE_CURRENT_EXPERIMENT,
  EDIT_CURRENT_EXPERIMENT,

  CREATE_EXPERIMENT_USING_CURRENT_VARIABLE,
  EDIT_EXPERIMENT_USING_CURRENT_VARIABLE,

  ABORT_CURRENT_EXPERIMENT,
  PAUSE_CURRENT_EXPERIMENT,
  APPROVE_CURRENT_EXPERIMENT,
  REJECT_CURRENT_EXPERIMENT,

  CREATE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE,
  APPROVE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE,
  REJECT_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE,

  CREATE_GROUP,
  EDIT_GROUP,
}

export const UserActionConfig: Record<
  UserAction,
  { action: _Action; text: string; scopeRequired: boolean }
> = {
  [UserAction.CREATE_A_VARIABLE]: {
    action: _Action.CREATE_VARIABLE,
    text: 'Creating a Variable',
    scopeRequired: false,
  },
  [UserAction.CREATE_VARIABLE_USING_CURRENT_TF]: {
    action: _Action.CREATE_VARIABLE,
    text: 'Selecting this option',
    scopeRequired: true,
  },
  [UserAction.EDIT_CURRENT_VARIABLE]: {
    action: _Action.UPDATE_VARIABLE,
    text: 'Editing this Variable',
    scopeRequired: true,
  },
  [UserAction.EDIT_CURRENT_VARIABLE_USING_CURRENT_TF]: {
    action: _Action.UPDATE_VARIABLE,
    text: 'Selecting this option',
    scopeRequired: true,
  },
  [UserAction.ARCHIVE_CURRENT_VARIABLE]: {
    action: _Action.ARCHIVE_VARIABLE,
    text: 'Archiving this Variable',
    scopeRequired: true,
  },
  [UserAction.UNDO_ARCHIVE_CURRENT_VARIABLE]: {
    action: _Action.ARCHIVE_VARIABLE,
    text: 'Undoing Archive of this Variable',
    scopeRequired: true,
  },
  [UserAction.CREATE_AN_EXPERIMENT]: {
    action: _Action.CREATE_EXPERIMENT,
    text: 'Creating an Experiment',
    scopeRequired: false,
  },
  [UserAction.CLONE_CURRENT_EXPERIMENT]: {
    action: _Action.CREATE_EXPERIMENT,
    text: 'Cloning this Experiment',
    scopeRequired: true,
  },
  [UserAction.EDIT_CURRENT_EXPERIMENT]: {
    action: _Action.UPDATE_EXPERIMENT,
    text: 'Editing this Experiment',
    scopeRequired: true,
  },
  [UserAction.CREATE_EXPERIMENT_USING_CURRENT_VARIABLE]: {
    action: _Action.CREATE_EXPERIMENT,
    text: 'Creating an Experiment with this Variable',
    scopeRequired: true,
  },
  [UserAction.EDIT_EXPERIMENT_USING_CURRENT_VARIABLE]: {
    action: _Action.UPDATE_EXPERIMENT,
    text: 'Editing an Experiment with this Variable',
    scopeRequired: true,
  },
  [UserAction.ABORT_CURRENT_EXPERIMENT]: {
    action: _Action.ABORT_EXPERIMENT,
    text: 'Aborting this Experiment',
    scopeRequired: true,
  },
  [UserAction.PAUSE_CURRENT_EXPERIMENT]: {
    action: _Action.PAUSING_EXPERIMENT,
    text: 'Pausing this Experiment',
    scopeRequired: true,
  },
  [UserAction.APPROVE_CURRENT_EXPERIMENT]: {
    action: _Action.APPROVE_EXPERIMENT,
    text: 'Approving this Experiment',
    scopeRequired: true,
  },
  [UserAction.REJECT_CURRENT_EXPERIMENT]: {
    action: _Action.REJECT_EXPERIMENT,
    text: 'Rejecting this Experiment',
    scopeRequired: true,
  },
  [UserAction.CREATE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE]: {
    action: _Action.CREATE_ROLLOUT_DIFF,
    text: 'Configuring Rollout for this Variable',
    scopeRequired: true,
  },
  [UserAction.APPROVE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE]: {
    action: _Action.UPDATE_ROLLOUT_DIFF_APPROVAL_STATE,
    text: 'Approving Changes for this Variable',
    scopeRequired: true,
  },
  [UserAction.REJECT_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE]: {
    action: _Action.UPDATE_ROLLOUT_DIFF_APPROVAL_STATE,
    text: 'Rejecting Changes for this Variable',
    scopeRequired: true,
  },
  [UserAction.CREATE_GROUP]: {
    action: _Action.CONFIGURE_GROUP,
    text: 'Creating a Group',
    scopeRequired: false,
  },
  [UserAction.EDIT_GROUP]: {
    action: _Action.CONFIGURE_GROUP,
    text: 'Editing a Group',
    scopeRequired: false,
  },
};

export const getPermissionConfig = (userAction: UserAction) => {
  const { action, text, scopeRequired } = UserActionConfig[userAction];
  return {
    role: _ActionPermission[action].role,
    scopeRequired,
    text,
  };
};

export const RoleNameToValue = {
  [RoleName.ADMIN]: 4,
  [RoleName.TECH_SUPPORT]: 3,
  [RoleName.PUBLISHER]: 2,
  [RoleName.EDITOR]: 1,
  [RoleName.VIEWER]: 0,
};

export const IgnoredRoles = [
  RoleName.ADMIN,
  RoleName.TECH_SUPPORT,
  RoleName.EXPERIMENT_COUNCIL,
  RoleName.EXPERIMENT_MODERATOR,
  RoleName.ROLLOUT_MODERATOR,
];

export type RoleType = keyof typeof RoleName;
export type Permission = TFRole;
